import { useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { useAuth } from 'hooks/UseAuth'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { useWorkspaces } from 'hooks/useWorkspaces'
import { UserService } from 'api/clients/UserService'
import {
  excludeCore,
  isValidDomain,
  redirectToFrontendDomainIfNecessary,
  workspaceDomainAttributesFromHostname,
  workspaceToRedirectTo,
} from 'utils/workspaceDomainUtils'
import { LOGIN_PATH, WORKSPACE_SELECTION_PATH } from 'paths'
import { useDefaultWorkspacePath } from 'utils/useDefaultWorkspacePath'
import { WorkspaceService } from 'api/services/WorkspaceService'
import { useCurrentWorkspace } from './useCurrentWorkspace'
import { ALMANAC_CORE_WORKSPACE_ID } from 'utils/constants'

interface Props {
  redirect?: boolean
  customDomain?: string | undefined
  disabled?: boolean
}

const useAuthRedirect = ({ redirect, customDomain, disabled }: Props = { redirect: true }) => {
  const auth = useAuth()
  const history = useHistory()
  const workspaces = useWorkspaces()
  const currentWorkspace = useCurrentWorkspace()
  const { defaultWorkspacePath } = useDefaultWorkspacePath()

  useEffect(() => {
    const performRedirect = async () => {
      const searchParams = new URLSearchParams(history.location.search)
      if (!searchParams.get('redirect_to')) {
        searchParams.set('redirect_to', history.location.pathname)
      }

      // if the user is logged in
      if (!!auth) {
        // Check if user is ICP-blocked and should be redirected to signup
        const icpBlocked = localStorage.getItem('icp_blocked')
        if (icpBlocked) {
          window.location.href = `/signup?businessType=${encodeURIComponent(icpBlocked)}` // eslint-disable-line no-restricted-properties
          return
        }
        if (customDomain != null) {
          return
        }

        if (workspaces?.length) {
          if (currentWorkspace) {
            let nextWorkspaceId = currentWorkspace.id

            // Try to not redirect to core as the first thing to do.
            // Parse the last_workspace_id and, if not present, try to find
            // any other workspace that is not core.
            if (nextWorkspaceId === ALMANAC_CORE_WORKSPACE_ID) {
              nextWorkspaceId = auth.last_workspace_id || auth.company_id || workspaces.filter(excludeCore)[0]?.id
            }

            const workspace = workspaceToRedirectTo({ workspaces, currentWorkspaceId: nextWorkspaceId })

            if (workspace) {
              const redirectLink = defaultWorkspacePath({
                id: workspace.id,
                guestReader: workspace.guest,
                homepageDocSlug: workspace.homepage_doc_slug,
              })
              // It might be necessary to redirect to default, if the custom domain cert is not valid for example
              if (!isValidDomain(workspace)) {
                redirectToFrontendDomainIfNecessary({
                  pathnameOvewrite: redirectLink,
                  searchParamsOvewrite: new URLSearchParams(),
                })
              }
              const promotion_code = searchParams.get('promotion_code')
              if (promotion_code) {
                history.push(`${redirectLink}?promotion_code=${promotion_code}`)
              } else {
                history.push(redirectLink)
              }
            } else {
              // When redirecting to core /home, force the path and lose the search params
              redirectToFrontendDomainIfNecessary({
                pathnameOvewrite: WORKSPACE_SELECTION_PATH,
                searchParamsOvewrite: new URLSearchParams(),
              })
              // getting there means there wasn't a redirect.
              // In that case, go to workspace selectiob
              history.push(WORKSPACE_SELECTION_PATH)
            }
          } else {
            const resp = await WorkspaceService.getWorkspaces()
            // Refetch workspaces to see if we have any. If it fails, push to login
            if (resp.type !== SUCCESS_RESPONSE_TYPE) {
              UserService.unsetAuthCookie()
              history.push(LOGIN_PATH, { search: searchParams.toString() })
            } else {
              // getting here means that the currentWorkspaceId is set to null or it has
              // a value that is not part of the loaded workspaces. This usually happens
              // when impersonating users.
              history.push(WORKSPACE_SELECTION_PATH)
            }
          }
        } else {
          // If we don't have workspaces, get them. When they are present, the
          // function will be triggered again by the useEffect
          const resp = await WorkspaceService.getWorkspaces()
          if (resp.type !== SUCCESS_RESPONSE_TYPE) {
            UserService.unsetAuthCookie()
            history.push(LOGIN_PATH, { search: searchParams.toString() })
          }
        }
      } else {
        // if the user is not logged in, redirect to the default domain if on custom domain
        const workspaceDomainAttributes = workspaceDomainAttributesFromHostname(window.getHostname())
        if (workspaceDomainAttributes?.name && !workspaceDomainAttributes.subdomain) {
          searchParams.set('domain', workspaceDomainAttributes.name)
        }
        redirectToFrontendDomainIfNecessary({
          pathnameOvewrite: LOGIN_PATH,
          searchParamsOvewrite: searchParams,
        })

        if (redirect) {
          // if not on custom domain, go to login
          history.push(LOGIN_PATH, { search: searchParams.toString() })
        }
      }
    }
    if (!disabled) {
      performRedirect()
    }
  }, [auth, workspaces, currentWorkspace, history, redirect, customDomain, disabled, defaultWorkspacePath])
}

export { useAuthRedirect }
